import { useEffect, useRef } from 'react';
import { useTenantStore } from 'src/stores/tenant';
import { useQueryClient } from '@tanstack/react-query';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';

/**
 * Hook to handle tenant initialization during app startup
 * This should be called once at the app level
 *
 * @param authReady - Whether authentication is ready (prevents race conditions)
 */
export const useTenantInitialization = (authReady: boolean = true) => {
  const queryClient = useQueryClient();
  const initializationAttempted = useRef(false);

  const {
    isInitialized,
    isLoading,
    currentTenant,
    authorizationError,
    actions: { initializeTenant },
  } = useTenantStore();

  useEffect(() => {
    // Wait for authentication to be ready before starting tenant initialization
    if (!authReady) {
      console.log(
        '🔄 Waiting for authentication before tenant initialization...',
      );
      return;
    }

    // Prevent multiple initialization attempts in strict mode
    if (initializationAttempted.current || isInitialized) return;

    console.log('🔄 Starting tenant initialization (auth ready)...');
    initializationAttempted.current = true;
    initializeTenant();
  }, [initializeTenant, isInitialized, authReady]);

  // Invalidate all queries when tenant changes
  useEffect(() => {
    if (currentTenant && isInitialized) {
      // Clear all cached queries when tenant changes
      queryClient.invalidateQueries();
    }
  }, [currentTenant, queryClient, isInitialized]);

  // In single-tenant mode, we don't need tenant selection UI
  // Instead, we let the app proceed and show the AdminTenants page for tenant creation
  const config = getRuntimeConfig();
  const isSingleTenantMode = config.VITE_TENANCY_MODE === 'single';

  return {
    isInitialized,
    isLoading,
    currentTenant,
    authorizationError,
    needsTenantSelection:
      isInitialized && !currentTenant && !isSingleTenantMode,
  };
};
